import { Box, Breadcrumbs, Button, Paper, Stack, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../shared/components/PageHeader';
import { FilterMultiSelect, type FilterOption } from '../../shared/components/FilterMultiSelect';
import {
  AiModelRatioChart,
  type AiModelRatioDatum,
} from '../../shared/components/AiModelRatioChart';
import {
  CategoryRatioTable,
  type CategoryRatioDatum,
} from '../../shared/components/CategoryRatioTable';
import {
  resources,
  type BrandVwAnalyticsRow,
  type BrandVwFieldFilter,
} from '../../core/services/resources';
import { useTenant } from '../../core/contexts/TenantContext';
import { flagFor } from '../../shared/utils/geo';

/** The cross-filtered dimensions, all starting empty. */
interface Selection {
  reportInstanceIds: number[];
  geoLocationIds: number[];
  aiModelIds: number[];
  categoryIds: number[];
  personaIds: number[];
  stageIds: number[];
  intentionTypes: string[];
  // sentiment brand ids resolve to tenant-brand names via the options endpoint.
  promptResponseSentimentBrandIds: number[];
  promptCustomFieldAs: string[];
  promptCustomFieldBs: string[];
  promptCustomFieldCs: string[];
}

const EMPTY_SELECTION: Selection = {
  reportInstanceIds: [],
  geoLocationIds: [],
  aiModelIds: [],
  categoryIds: [],
  personaIds: [],
  stageIds: [],
  intentionTypes: [],
  promptResponseSentimentBrandIds: [],
  promptCustomFieldAs: [],
  promptCustomFieldBs: [],
  promptCustomFieldCs: [],
};

/**
 * Visibility view scoped to a specific report template
 * (`/report-templates/:id/visibility`). The top panel shows the active
 * tenant → line of business → report template, followed by a row of
 * cross-filtered multi-select dropdowns. Picking a value in one dropdown
 * narrows every other dropdown to the matching combinations (faceted
 * search) via `GET /flat-views/report-instance-filter-vm/options`.
 */
export function ReportTemplateVisibility() {
  const { id } = useParams();
  const templateId = Number(id);
  const { activeTenantName } = useTenant();

  const [selection, setSelection] = useState<Selection>(EMPTY_SELECTION);
  const hasSelection = useMemo(
    () => Object.values(selection).some((arr) => arr.length > 0),
    [selection],
  );

  // Selection committed via "Show" — the statistics below read from this
  // (not the live `selection`), so results only refresh on an explicit Show
  // while the dropdowns themselves cross-filter live. "Show" requires at
  // least one report instance.
  const [appliedSelection, setAppliedSelection] = useState<Selection>(EMPTY_SELECTION);
  const canShow = selection.reportInstanceIds.length > 0;
  const hasApplied = appliedSelection.reportInstanceIds.length > 0;

  // --- breadcrumb scope (tenant → LOB → report template) -------------------
  const template = useQuery({
    queryKey: ['report-templates', templateId],
    queryFn: () => resources.reportTemplates.get(templateId),
    enabled: Number.isFinite(templateId),
  });
  const lineOfBizId = template.data?.lineOfBizId;
  const lineOfBiz = useQuery({
    queryKey: ['lines-of-business', lineOfBizId],
    queryFn: () => resources.linesOfBusiness.get(lineOfBizId as number),
    enabled: lineOfBizId != null,
  });

  // --- label catalogs (resolve numeric/string ids → human names) -----------
  const geoCatalog = useQuery({
    queryKey: ['geo-locations', 'select'],
    queryFn: () => resources.geoLocations.list({ pageSize: 500, sort: 'name' }),
  });
  const geoById = useMemo(() => {
    const map = new Map<number, { code: string; name: string }>();
    for (const g of geoCatalog.data?.data ?? []) map.set(g.id, { code: g.code, name: g.name });
    return map;
  }, [geoCatalog.data]);

  const aiModelCatalog = useQuery({
    queryKey: ['ai-models', 'select'],
    queryFn: () => resources.aiModels.list({ pageSize: 500, sort: 'name' }),
  });
  // `report_instance_flat_filter.ai_model_id` holds the numeric `AIModel.id`.
  const aiModelNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of aiModelCatalog.data?.data ?? []) map.set(m.id, m.name);
    return map;
  }, [aiModelCatalog.data]);

  const categoryCatalog = useQuery({
    queryKey: ['line-of-biz-categories', 'select', lineOfBizId],
    queryFn: () =>
      resources.lineOfBizCategories.list({
        pageSize: 500,
        sort: 'name',
        filter: lineOfBizId != null ? { lineOfBizId } : undefined,
      }),
    enabled: lineOfBizId != null,
  });
  const categoryNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of categoryCatalog.data?.data ?? []) map.set(c.id, c.name);
    return map;
  }, [categoryCatalog.data]);

  const personaCatalog = useQuery({
    queryKey: ['line-of-biz-personas', 'select', lineOfBizId],
    queryFn: () =>
      resources.lineOfBizPersonas.list({
        pageSize: 500,
        sort: 'name',
        filter: lineOfBizId != null ? { lineOfBizId } : undefined,
      }),
    enabled: lineOfBizId != null,
  });
  const personaNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of personaCatalog.data?.data ?? []) map.set(p.id, p.name);
    return map;
  }, [personaCatalog.data]);

  const stageCatalog = useQuery({
    queryKey: ['prompt-funnel-stages', 'select'],
    queryFn: () => resources.promptFunnelStages.list({ pageSize: 500, sort: 'name' }),
  });
  const stageNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of stageCatalog.data?.data ?? []) map.set(s.id, s.name);
    return map;
  }, [stageCatalog.data]);

  // --- cross-filtered options ----------------------------------------------
  // Refetched whenever the selection changes; `keepPreviousData` keeps the
  // dropdowns populated (no flicker to empty) during the refetch.
  const optionsQuery = useQuery({
    queryKey: ['report-instance-filter-options', templateId, selection],
    queryFn: () =>
      resources.reportInstanceFilterOptions({ reportTemplateId: templateId, ...selection }),
    enabled: Number.isFinite(templateId),
    placeholderData: keepPreviousData,
  });
  const opts = optionsQuery.data;
  const loading = optionsQuery.isFetching;

  const geoLabel = (geoId: number) => {
    const geo = geoById.get(geoId);
    return geo ? `${flagFor(geo.code)} ${geo.name}` : `#${geoId}`;
  };

  const reportInstanceOptions = useMemo<FilterOption<number>[]>(
    () => (opts?.reportInstanceIds ?? []).map((o) => ({ value: o.id, label: `#${o.id}` })),
    [opts],
  );
  const geoOptions = useMemo<FilterOption<number>[]>(
    () => (opts?.geoLocationIds ?? []).map((o) => ({ value: o.id, label: geoLabel(o.id) })),
    [opts, geoById],
  );
  const aiModelOptions = useMemo<FilterOption<number>[]>(
    () =>
      (opts?.aiModelIds ?? []).map((o) => ({ value: o.id, label: aiModelNameById.get(o.id) ?? o.name })),
    [opts, aiModelNameById],
  );
  const categoryOptions = useMemo<FilterOption<number>[]>(
    () =>
      (opts?.categoryIds ?? []).map((o) => ({ value: o.id, label: categoryNameById.get(o.id) ?? o.name })),
    [opts, categoryNameById],
  );
  const personaOptions = useMemo<FilterOption<number>[]>(
    () =>
      (opts?.personaIds ?? []).map((o) => ({ value: o.id, label: personaNameById.get(o.id) ?? o.name })),
    [opts, personaNameById],
  );
  const stageOptions = useMemo<FilterOption<number>[]>(
    () => (opts?.stageIds ?? []).map((o) => ({ value: o.id, label: stageNameById.get(o.id) ?? o.name })),
    [opts, stageNameById],
  );
  const intentionOptions = useMemo<FilterOption<string>[]>(
    () => (opts?.intentionTypes ?? []).map((v) => ({ value: v, label: v })),
    [opts],
  );
  // Names come resolved from the options endpoint (tenant-brand names).
  const sentimentBrandOptions = useMemo<FilterOption<number>[]>(
    () =>
      (opts?.promptResponseSentimentBrandIds ?? []).map((o) => ({
        value: o.id,
        label: o.name || `#${o.id}`,
      })),
    [opts],
  );
  const customFieldAOptions = useMemo<FilterOption<string>[]>(
    () => (opts?.promptCustomFieldAs ?? []).map((v) => ({ value: v, label: v })),
    [opts],
  );
  const customFieldBOptions = useMemo<FilterOption<string>[]>(
    () => (opts?.promptCustomFieldBs ?? []).map((v) => ({ value: v, label: v })),
    [opts],
  );
  const customFieldCOptions = useMemo<FilterOption<string>[]>(
    () => (opts?.promptCustomFieldCs ?? []).map((v) => ({ value: v, label: v })),
    [opts],
  );

  // --- owner-share statistics (per dimension), all fired in parallel -------
  // The owner-share ratio per group = count(records where associationType =
  // 'OWNER') / count(reportInstancePromptId records). Each block is its own
  // query so they resolve independently and the page paints incrementally.
  const enabled = hasApplied && Number.isFinite(templateId);

  const aiModelAnalytics = useQuery({
    queryKey: ['visibility-ratio', 'aiModelId', templateId, appliedSelection],
    queryFn: () =>
      fetchRatioByDimension('aiModelId', buildAnalyticsFilters(appliedSelection)),
    enabled,
    placeholderData: keepPreviousData,
  });

  const categoryAnalytics = useQuery({
    queryKey: ['visibility-ratio', 'categoryId', templateId, appliedSelection],
    queryFn: () =>
      fetchRatioByDimension('categoryId', buildAnalyticsFilters(appliedSelection)),
    enabled,
    placeholderData: keepPreviousData,
  });

  const aiModelChartData = useMemo<AiModelRatioDatum[]>(
    () =>
      computeRatioRows(aiModelAnalytics.data, 'aiModelId').map((r) => ({
        id: r.id,
        label: aiModelNameById.get(Number(r.id)) ?? (r.id || '—'),
        percent: r.percent,
      })),
    [aiModelAnalytics.data, aiModelNameById],
  );

  const categoryRows = useMemo<CategoryRatioDatum[]>(
    () =>
      computeRatioRows(categoryAnalytics.data, 'categoryId').map((r) => ({
        id: r.id,
        label: categoryNameById.get(Number(r.id)) ?? (r.id ? `#${r.id}` : 'Uncategorized'),
        percent: r.percent,
      })),
    [categoryAnalytics.data, categoryNameById],
  );

  const tenantName = activeTenantName ?? 'Tenant';
  const lobName = lineOfBiz.data?.name ?? (lineOfBizId != null ? `LOB #${lineOfBizId}` : '—');
  const templateName = template.data?.name ?? `Report template #${templateId}`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader title="Visibility" />

      {/* Top panel: active scope + cross-filtered dropdown row. */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {tenantName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {lobName}
          </Typography>
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {templateName}
          </Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: 1.5,
          }}
        >
          <FilterMultiSelect
            label="Report instances"
            options={reportInstanceOptions}
            value={selection.reportInstanceIds}
            onChange={(v) => setSelection((s) => ({ ...s, reportInstanceIds: v }))}
            loading={loading}
          />
          <FilterMultiSelect
            label="Geo locations"
            options={geoOptions}
            value={selection.geoLocationIds}
            onChange={(v) => setSelection((s) => ({ ...s, geoLocationIds: v }))}
            loading={loading}
          />
          <FilterMultiSelect
            label="AI models"
            options={aiModelOptions}
            value={selection.aiModelIds}
            onChange={(v) => setSelection((s) => ({ ...s, aiModelIds: v }))}
            loading={loading}
          />
          <FilterMultiSelect
            label="Categories"
            options={categoryOptions}
            value={selection.categoryIds}
            onChange={(v) => setSelection((s) => ({ ...s, categoryIds: v }))}
            loading={loading}
          />
          <FilterMultiSelect
            label="Personas"
            options={personaOptions}
            value={selection.personaIds}
            onChange={(v) => setSelection((s) => ({ ...s, personaIds: v }))}
            loading={loading}
          />
          <FilterMultiSelect
            label="Funnel stages"
            options={stageOptions}
            value={selection.stageIds}
            onChange={(v) => setSelection((s) => ({ ...s, stageIds: v }))}
            loading={loading}
          />
          <FilterMultiSelect
            label="Intention types"
            options={intentionOptions}
            value={selection.intentionTypes}
            onChange={(v) => setSelection((s) => ({ ...s, intentionTypes: v }))}
            loading={loading}
          />
          <FilterMultiSelect
            label="Sentiment brands"
            options={sentimentBrandOptions}
            value={selection.promptResponseSentimentBrandIds}
            onChange={(v) => setSelection((s) => ({ ...s, promptResponseSentimentBrandIds: v }))}
            loading={loading}
          />
          <FilterMultiSelect
            label="Custom field A"
            options={customFieldAOptions}
            value={selection.promptCustomFieldAs}
            onChange={(v) => setSelection((s) => ({ ...s, promptCustomFieldAs: v }))}
            loading={loading}
          />
          <FilterMultiSelect
            label="Custom field B"
            options={customFieldBOptions}
            value={selection.promptCustomFieldBs}
            onChange={(v) => setSelection((s) => ({ ...s, promptCustomFieldBs: v }))}
            loading={loading}
          />
          <FilterMultiSelect
            label="Custom field C"
            options={customFieldCOptions}
            value={selection.promptCustomFieldCs}
            onChange={(v) => setSelection((s) => ({ ...s, promptCustomFieldCs: v }))}
            loading={loading}
          />
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              size="small"
              disabled={!hasSelection}
              onClick={() => {
                setSelection(EMPTY_SELECTION);
                setAppliedSelection(EMPTY_SELECTION);
              }}
            >
              Clear filters
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={!canShow}
              onClick={() => setAppliedSelection(selection)}
            >
              Show
            </Button>
          </Stack>
        </Box>
      </Paper>

      {hasApplied ? (
        <Stack spacing={3}>
          {/* AI-model statistics — owner-share ratio per model. */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              AI model visibility
            </Typography>
            <AiModelRatioChart data={aiModelChartData} loading={aiModelAnalytics.isFetching} />
          </Box>

          {/* Category statistics — same owner-share ratio per category. */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Category visibility
            </Typography>
            <CategoryRatioTable data={categoryRows} loading={categoryAnalytics.isFetching} />
          </Box>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Select at least one report instance and press <strong>Show</strong> to see visibility
          statistics.
        </Typography>
      )}
    </Box>
  );
}

type AnalyticsFilters = { reportInstanceIds: number[]; additional: BrandVwFieldFilter[] };

/** Group-key normalizer — view columns arrive as string|number|null. */
function groupKey(value: string | number | null | undefined): string {
  return value == null ? '' : String(value);
}

/**
 * Run the owner-share aggregation for one dimension in a single grouped query:
 * the denominator (all records) and the numerator (associationType = 'OWNER')
 * are computed together via a conditional `FILTER (WHERE …)` aggregation.
 */
async function fetchRatioByDimension(
  field: string,
  filters: AnalyticsFilters,
): Promise<BrandVwAnalyticsRow[]> {
  return resources.reportInstanceFlatBrandAnalytics({
    filters,
    groupBy: [field],
    aggregations: [
      { fn: 'count', field: 'reportInstancePromptId', alias: 'total' },
      {
        fn: 'count',
        field: 'reportInstancePromptId',
        alias: 'owner',
        where: [{ field: 'associationType', values: ['OWNER'] }],
      },
    ],
  });
}

interface RatioRow {
  id: string;
  total: number;
  owner: number;
  percent: number;
}

/** Compute owner-share % per group from each row's `total` + `owner`, sorted desc. */
function computeRatioRows(
  rows: BrandVwAnalyticsRow[] | undefined,
  field: string,
): RatioRow[] {
  return (rows ?? [])
    .map((r) => {
      const id = groupKey(r[field]);
      const total = Number(r.total ?? 0);
      const owner = Number(r.owner ?? 0);
      return { id, total, owner, percent: total > 0 ? (owner / total) * 100 : 0 };
    })
    .sort((a, b) => b.percent - a.percent);
}

/**
 * Build the analytics filters from the applied selection: report instances are
 * the primary scope (each instance already belongs to one template, so the rows
 * can't leak across templates), and every other dimension becomes an ad-hoc
 * `IN` filter. Tenant scope is applied server-side from the request context.
 */
function buildAnalyticsFilters(sel: Selection): AnalyticsFilters {
  const additional: BrandVwFieldFilter[] = [];
  const add = (field: string, values: Array<string | number>) => {
    if (values.length > 0) additional.push({ field, values });
  };
  add('geoLocationId', sel.geoLocationIds);
  add('aiModelId', sel.aiModelIds);
  add('categoryId', sel.categoryIds);
  add('personaId', sel.personaIds);
  add('stageId', sel.stageIds);
  add('intentionType', sel.intentionTypes);
  add('promptCustomFieldA', sel.promptCustomFieldAs);
  add('promptCustomFieldB', sel.promptCustomFieldBs);
  add('promptCustomFieldC', sel.promptCustomFieldCs);
  // NOTE: promptResponseSentimentBrandIds is intentionally NOT a flat_brand
  // analytics filter — that column exists only on report_instance_flat_source,
  // not report_instance_flat_brand (which backs these stats). It still
  // cross-filters the dropdown options via the /options endpoint.
  return { reportInstanceIds: sel.reportInstanceIds, additional };
}
